/**
 * The power course, at `/grid`: the Wren Valley grid (docs/grid.md).
 *
 * The learner runs a small town's distribution network. Each level is a job
 * from the control room; they change the grid (taps, conductors, switches,
 * a customer's charging power), scrub through the day to see what it does,
 * and press Run the day, which plays all 96 quarter-hours across the diagram
 * and then checks the level's goals against what happened. A failed goal
 * jumps the clock to its worst moment and selects the place it happened.
 *
 * The simulator is web/src/lib/grid/model.ts, a real radial power flow; the
 * levels are web/src/lib/grid/levels.ts. English only for now, on the
 * owner's instruction (2026-10-16), so the words are here rather than in the
 * string catalogue. Its own entry, mounted instead of App: no virtual
 * machine, so it opens at once.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Brand, Button, LinkButton, Tag } from '../../ui';
import { Corner } from '../Corner';
import { ABOUT_PATH } from '../../lib/landing';
import { track as record } from '../../lib/events';
import { sound } from '../../lib/sound';
import { loadPassed, savePassed, TRACK_PATHS } from '../../lib/tracks';
import {
  BUSES,
  CONDUCTORS,
  CONDUCTOR_IDS,
  HUB_POWER_STOPS,
  LINES,
  OLTC_MAX,
  OLTC_MIN,
  OLTC_STEP,
  STEPS,
  STREETS,
  STREET_TAPS,
  TRANSFORMERS,
  clock,
  cost,
  runDay,
  type BusId,
  type ConductorId,
  type GridSettings,
  type LineId,
  type Snapshot,
  type StreetId,
} from '../../lib/grid/model';
import { LEVELS, V_MAX, V_MIN, grade, type GridLevel, type Verdict } from '../../lib/grid/levels';
import { GRID_BRIEFS, type GridBrief } from '../../lib/agent';
import { AgentPanel, howLine } from '../tracks/engine';

const STORE = (id: string) => `engineeros.grid.${id}`;
const RUN_MS = 4200;

function loadSettings(level: GridLevel): GridSettings {
  try {
    const raw = localStorage.getItem(STORE(level.id));
    if (raw) return { ...structuredClone(level.start), ...(JSON.parse(raw) as GridSettings) };
  } catch {
    /* fall through */
  }
  return structuredClone(level.start);
}

function saveSettings(level: GridLevel, s: GridSettings): void {
  try {
    localStorage.setItem(STORE(level.id), JSON.stringify(s));
  } catch {
    /* private mode */
  }
}

/** What the inspector is showing. */
type Selection =
  | { kind: 'street'; id: StreetId }
  | { kind: 'tx'; id: StreetId | 'T1' }
  | { kind: 'line'; id: LineId }
  | { kind: 'hub' };

const COMING = ['Too much sun', 'Shave the peak', 'A fault on Feeder B', 'The generator trips', "Iris's switching plan"];

export function GridCourse() {
  const [passed, setPassed] = useState<Set<string>>(() => loadPassed('grid'));
  const firstOpen = LEVELS.find((l) => !passed.has(l.id)) ?? LEVELS[0]!;
  const [levelId, setLevelId] = useState<string>(() => {
    const hash = window.location.hash.slice(1);
    return LEVELS.some((l) => l.id === hash) ? hash : firstOpen.id;
  });
  const level = LEVELS.find((l) => l.id === levelId) ?? LEVELS[0]!;

  useEffect(() => {
    document.title = 'The Wren Valley grid · EngineerOS';
  }, []);

  const pass = useCallback(
    (id: string) => {
      if (passed.has(id)) return;
      const next = new Set(passed);
      next.add(id);
      setPassed(next);
      savePassed('grid', next);
      record('practice', { detail: `grid:${id}` });
      if (LEVELS.every((l) => next.has(l.id))) record('practice', { detail: 'grid:done' });
    },
    [passed],
  );

  const open = (id: string) => {
    setLevelId(id);
    window.history.replaceState(null, '', `#${id}`);
    sound.click();
  };

  return (
    <>
      <Corner />
      <div className="grid-course">
        <header className="foundations__bar">
          <Brand href={ABOUT_PATH} />
        </header>
        <header className="grid-course__head">
          <p className="foundations__kicker">Power engineering · early access</p>
          <h1 className="foundations__title">The Wren Valley grid</h1>
          <p className="foundations__lede">
            You run the electricity network of a small town: a substation, two 11 kV feeders and five streets. Each
            level is a job from the control room. Change the grid, scrub through the day, and run it: the simulator
            solves the whole network every quarter-hour and tells you what happened on every street.
          </p>
          <p className="foundations__honest">
            A real power flow on a simplified network: balanced phases, loads that follow a daily curve, taps set by
            hand. Every level says what it leaves out. It teaches how grids behave; it does not qualify anyone to
            operate one.
          </p>
        </header>

        <nav className="grid-levels" aria-label="Levels">
          {LEVELS.map((l) => (
            <button
              key={l.id}
              type="button"
              className={`grid-levels__item${l.id === level.id ? ' is-on' : ''}${passed.has(l.id) ? ' is-done' : ''}`}
              aria-current={l.id === level.id ? 'page' : undefined}
              onClick={() => open(l.id)}
            >
              <span className="grid-levels__n">Level {l.n}</span>
              <span className="grid-levels__title">{l.title}</span>
              {passed.has(l.id) && <span className="grid-levels__tick">Done</span>}
            </button>
          ))}
          {COMING.map((title, k) => (
            <span key={title} className="grid-levels__item is-locked">
              <span className="grid-levels__n">Level {LEVELS.length + k + 1}</span>
              <span className="grid-levels__title">{title}</span>
              <span className="grid-levels__tick">Coming</span>
            </span>
          ))}
        </nav>

        <LevelView key={level.id} level={level} done={passed.has(level.id)} onPass={() => pass(level.id)} onNext={open} />

        <p className="grid-course__back">
          <a href={TRACK_PATHS.power}>The five-task Power and Energy preview</a> ·{' '}
          <a href={TRACK_PATHS.foundations}>Foundations</a>
        </p>
      </div>
    </>
  );
}

/* ── One level ─────────────────────────────────────────────────────────── */

function LevelView({ level, done, onPass, onNext }: { level: GridLevel; done: boolean; onPass(): void; onNext(id: string): void }) {
  const [settings, setSettings] = useState<GridSettings>(() => loadSettings(level));
  const [step, setStep] = useState(76);
  const [selected, setSelected] = useState<Selection>({ kind: 'street', id: 'elm' });
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [running, setRunning] = useState(false);
  const [how, setHow] = useState<ReadonlySet<'hand' | 'iris'>>(() => new Set());
  const raf = useRef(0);

  const day = useMemo(() => runDay(settings), [settings]);
  const snap = day.ok ? day.steps[step]! : null;

  const change = (patch: (s: GridSettings) => void, who: 'hand' | 'iris' = 'hand') => {
    if (running) return;
    const next = structuredClone(settings);
    patch(next);
    setSettings(next);
    saveSettings(level, next);
    setVerdict(null);
    if (!how.has(who)) setHow(new Set([...how, who]));
    sound.click();
  };

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const run = () => {
    if (running) return;
    setRunning(true);
    setVerdict(null);
    sound.click();
    const v = grade(level, settings);
    const start = performance.now();
    const reduced = (() => {
      try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch {
        return false;
      }
    })();
    const finish = () => {
      setRunning(false);
      setVerdict(v);
      if (v.ok) {
        sound.clear();
        onPass();
      } else {
        sound.fail();
        jumpToWorst(v);
      }
    };
    if (reduced || !v.day.ok) {
      finish();
      return;
    }
    const tick = (now: number) => {
      // A frame's timestamp can be a little earlier than the click that started the run.
      const t = Math.max(0, Math.min(1, (now - start) / RUN_MS));
      setStep(Math.min(STEPS - 1, Math.floor(t * STEPS)));
      if (t < 1) raf.current = requestAnimationFrame(tick);
      else finish();
    };
    raf.current = requestAnimationFrame(tick);
  };

  /** Put the clock and the inspector on the failure the learner most needs to see. */
  const jumpToWorst = (v: Verdict) => {
    if (!v.day.ok) return;
    const failing = v.goals.find((g) => !g.ok);
    if (!failing) return;
    let best = { step: 0, score: -1, sel: null as Selection | null };
    v.day.steps.forEach((snap, k) => {
      if (failing.id === 'voltage') {
        for (const st of STREETS) {
          const over = Math.max(V_MIN - snap.v[st], snap.v[st] - V_MAX);
          if (over > best.score) best = { step: k, score: over, sel: { kind: 'street', id: st } };
        }
      }
      if (failing.id === 'loading') {
        for (const [id, load] of Object.entries(snap.loading)) {
          if (load > best.score) {
            const isLine = LINES.some((l) => l.id === id);
            best = { step: k, score: load, sel: isLine ? { kind: 'line', id: id as LineId } : { kind: 'tx', id: id as StreetId | 'T1' } };
          }
        }
      }
    });
    if (best.sel) {
      setStep(best.step);
      setSelected(best.sel);
    }
  };

  const reset = () => {
    change((s) => Object.assign(s, structuredClone(level.start)));
    setHow(new Set());
  };

  const spent = cost(settings);
  const next = LEVELS[LEVELS.findIndex((l) => l.id === level.id) + 1];

  return (
    <section className="grid-level" aria-labelledby="grid-level-title">
      <div className="grid-ticket">
        <p className="grid-ticket__from">{level.from}</p>
        <h2 className="grid-ticket__title" id="grid-level-title">
          Level {level.n} · {level.title} {done && <Tag tone="on">Done</Tag>}
        </h2>
        {level.brief.map((p) => (
          <p key={p}>{p}</p>
        ))}
        <p className="grid-ticket__hint">{level.hint}</p>
        <ul className="grid-goals">
          {level.goals.map((g) => {
            const r = verdict?.goals.find((x) => x.id === g.id);
            return (
              <li key={g.id} className={r ? (r.ok ? 'is-ok' : 'is-fail') : ''}>
                <span className="grid-goals__mark" aria-hidden="true">
                  {r ? (r.ok ? '✓' : '✕') : '·'}
                </span>
                <span>
                  {g.text}
                  {r && <span className="grid-goals__detail">{r.detail}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <AgentPanel<true>
        briefs={GRID_BRIEFS[level.id] ?? []}
        ctx={{}}
        disabled={running}
        onApply={(b) => change((s) => (b as GridBrief).edit(s), 'iris')}
      />

      <div className={`grid-work${verdict?.ok ? ' is-pass' : ''}${running ? ' is-running' : ''}`}>
        <div className="grid-board">
          <Diagram settings={settings} snap={snap} level={level} selected={selected} onSelect={setSelected} />
          <div className="grid-clock">
            <span className="grid-clock__time" aria-live="off">
              {clock(step)}
            </span>
            <input
              type="range"
              min={0}
              max={STEPS - 1}
              value={step}
              aria-label="Time of day"
              aria-valuetext={clock(step)}
              disabled={running}
              onChange={(e) => setStep(Number(e.target.value))}
            />
            <Button variant="solid" onClick={run} disabled={running}>
              {running ? 'Running the day…' : 'Run the day'}
            </Button>
          </div>
          <div className="grid-status">
            {snap ? (
              <span>
                Demand {snap.demandMw.toFixed(2)} MW · losses {(snap.lossMw * 1000).toFixed(0)} kW · substation {snap.v.sub.toFixed(3)} pu
              </span>
            ) : (
              <span className="grid-status__bad">{!day.ok && day.reason === 'loop' ? 'Two paths to the same street: open a switch.' : 'Part of the town has no supply.'}</span>
            )}
            {level.controls.conductors && (
              <span className={spent > level.budget ? 'grid-status__bad' : ''}>
                Spent ${spent.toLocaleString('en-US')} of ${level.budget.toLocaleString('en-US')}
              </span>
            )}
            <button type="button" className="btn btn-quiet" onClick={reset} disabled={running}>
              Reset level
            </button>
          </div>
        </div>
        <Inspector level={level} settings={settings} day={day} step={step} selected={selected} change={change} />
      </div>

      {verdict && (
        <p className={verdict.ok ? 'grid-verdict is-ok' : 'grid-verdict is-fail'} role="status">
          {verdict.ok ? 'Every goal met. The town got through the day.' : 'Not yet. The clock is on the worst moment of the first goal that failed.'}
        </p>
      )}

      {done && (
        <div className="fstep__debrief grid-debrief">
          {how.size > 0 && <p className="fstep__how">{howLine(how)}</p>}
          {level.debrief.map((p) => (
            <p key={p}>{p}</p>
          ))}
          <p className="fstep__leaves">
            <span className="label">What this model leaves out</span>
            {level.leavesOut}
          </p>
          {next ? (
            <Button variant="solid" onClick={() => onNext(next.id)}>
              Next: {next.title}
            </Button>
          ) : (
            <p>
              That is the first part of the course. Levels {LEVELS.length + 1} to {LEVELS.length + COMING.length} are being built.{' '}
              <LinkButton href={`${TRACK_PATHS.power}#disciplines`}>Ask for the full course</LinkButton>
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/* ── The single-line diagram ───────────────────────────────────────────── */

const POS: Record<BusId, [number, number]> = {
  supply: [40, 235],
  sub: [150, 235],
  a1: [310, 150],
  a2: [480, 150],
  a3: [680, 150],
  b1: [380, 320],
  b2: [680, 320],
  mill: [310, 40],
  oak: [480, 40],
  elm: [680, 40],
  harbour: [380, 430],
  station: [680, 430],
};

const FEEDER_A_Y = 150;
const FEEDER_B_Y = 320;
const BUSBAR_X = 150;

function lineGeometry(id: LineId): { d: string; mid: [number, number] } {
  const l = LINES.find((x) => x.id === id)!;
  const from = l.from === 'sub' ? [BUSBAR_X, l.to.startsWith('a') ? FEEDER_A_Y : FEEDER_B_Y] : POS[l.from];
  const to = POS[l.to];
  return { d: `M ${from[0]} ${from[1]} L ${to[0]} ${to[1]}`, mid: [(from[0]! + to[0]) / 2, (from[1]! + to[1]) / 2] };
}

function loadClass(load: number | undefined): string {
  if (load === undefined) return 'is-dead';
  if (load > 1) return 'is-over';
  if (load > 0.85) return 'is-high';
  return '';
}

function voltClass(v: number | undefined): string {
  if (v === undefined || v === 0) return 'is-dead';
  if (v < V_MIN || v > V_MAX) return 'is-over';
  if (v < V_MIN + 0.01 || v > V_MAX - 0.01) return 'is-high';
  return '';
}

function Diagram({
  settings,
  snap,
  level,
  selected,
  onSelect,
}: {
  settings: GridSettings;
  snap: Snapshot | null;
  level: GridLevel;
  selected: Selection;
  onSelect(s: Selection): void;
}) {
  const is = (s: Selection) => JSON.stringify(s) === JSON.stringify(selected);
  const tx = (id: StreetId | 'T1') => {
    const t = TRANSFORMERS.find((x) => x.id === id)!;
    const [x, y] = id === 'T1' ? [95, 235] : [POS[t.from][0], (POS[t.from][1] + POS[t.to][1]) / 2];
    const tap = id === 'T1' ? settings.oltc : (STREET_TAPS[settings.taps[id]] ?? 0) * 100;
    return (
      <g
        key={id}
        className={`grid-el grid-tx ${loadClass(snap?.loading[id])}${is({ kind: 'tx', id }) ? ' is-selected' : ''}`}
        role="button"
        tabIndex={0}
        aria-label={`${t.name}, tap ${tap > 0 ? '+' : ''}${tap}${id === 'T1' ? '' : '%'}`}
        onClick={() => onSelect({ kind: 'tx', id })}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect({ kind: 'tx', id })}
      >
        {id === 'T1' ? (
          <>
            <circle cx={x - 7} cy={y} r={11} />
            <circle cx={x + 7} cy={y} r={11} />
          </>
        ) : (
          <>
            <circle cx={x} cy={y - 7} r={10} />
            <circle cx={x} cy={y + 7} r={10} />
          </>
        )}
        {tap !== 0 && (
          <text className="grid-tx__tap" x={x + (id === 'T1' ? 0 : 16)} y={id === 'T1' ? y - 18 : y + 4} textAnchor={id === 'T1' ? 'middle' : 'start'}>
            {tap > 0 ? '+' : ''}
            {id === 'T1' ? tap : `${tap}%`}
          </text>
        )}
      </g>
    );
  };

  const line = (id: LineId) => {
    const g = lineGeometry(id);
    const closed = settings.closed[id];
    const cond = id === 'tie' ? 'light' : settings.conductors[id];
    const load = closed ? snap?.loading[id] : undefined;
    return (
      <g
        key={id}
        className={`grid-el grid-line is-${cond} ${closed ? loadClass(load) : 'is-open'}${is({ kind: 'line', id }) ? ' is-selected' : ''}`}
        role="button"
        tabIndex={0}
        aria-label={`${LINES.find((l) => l.id === id)!.name}, ${closed ? 'closed' : 'open'}`}
        onClick={() => onSelect({ kind: 'line', id })}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect({ kind: 'line', id })}
      >
        <path className="grid-line__hit" d={g.d} />
        <path className="grid-line__wire" d={g.d} />
        {closed && load !== undefined && load > 0.02 && <path className="grid-line__flow" d={g.d} style={{ animationDuration: `${Math.max(0.25, 1.4 - load)}s` }} />}
        <rect className={`grid-switch${closed ? ' is-closed' : ''}`} x={g.mid[0] - 6} y={g.mid[1] - 6} width={12} height={12} />
        {load !== undefined && (
          <text className="grid-line__pct" x={g.mid[0]} y={g.mid[1] + (id === 'tie' ? 4 : -12)} dx={id === 'tie' ? 14 : 0} textAnchor={id === 'tie' ? 'start' : 'middle'}>
            {Math.round(load * 100)}%
          </text>
        )}
      </g>
    );
  };

  const street = (id: StreetId) => {
    const [x, y] = POS[id];
    const v = snap?.v[id];
    const name = BUSES.find((b) => b.id === id)!.name;
    const below = y > 300;
    return (
      <g
        key={id}
        className={`grid-el grid-street ${voltClass(v)}${is({ kind: 'street', id }) ? ' is-selected' : ''}`}
        role="button"
        tabIndex={0}
        aria-label={`${name}, ${v ? v.toFixed(3) : 'no supply'}`}
        onClick={() => onSelect({ kind: 'street', id })}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect({ kind: 'street', id })}
      >
        <rect className="grid-street__box" x={x - 52} y={y - 18} width={104} height={36} rx={3} />
        {[-30, -12, 6, 24].map((dx, k) => (
          <path key={dx} className="grid-street__house" d={`M ${x + dx - 6} ${y + 10} v -8 l 6 -6 l 6 6 v 8 z`} opacity={v ? 1 - k * 0.12 : 0.3} />
        ))}
        <text className="grid-street__name" x={x} y={below ? y + 34 : y - 24} textAnchor="middle">
          {name}
        </text>
        <text className="grid-street__v" x={x + 58} y={y + 5}>
          {v ? v.toFixed(3) : '—'}
        </text>
      </g>
    );
  };

  return (
    <svg className="grid-diagram" viewBox="0 0 800 470" role="group" aria-label="The Wren Valley network">
      {/* Supply and the substation busbar. */}
      <g className="grid-supply">
        <circle cx={40} cy={235} r={16} />
        <text x={40} y={268} textAnchor="middle">33 kV</text>
        <line x1={56} x2={82} y1={235} y2={235} />
        <line x1={108} x2={BUSBAR_X} y1={235} y2={235} />
      </g>
      <line className="grid-busbar" x1={BUSBAR_X} x2={BUSBAR_X} y1={FEEDER_A_Y - 6} y2={FEEDER_B_Y + 6} />
      <text className="grid-label" x={BUSBAR_X + 8} y={FEEDER_A_Y - 12}>Feeder A</text>
      <text className="grid-label" x={BUSBAR_X + 8} y={FEEDER_B_Y + 22}>Feeder B</text>
      {(['sub-a1', 'a1-a2', 'a2-a3', 'sub-b1', 'b1-b2', 'tie'] as LineId[]).map(line)}
      {/* Drops from the poles to the street transformers. */}
      {STREETS.map((id) => {
        const t = TRANSFORMERS.find((x) => x.id === id)!;
        const [x, y] = POS[t.from];
        const [, sy] = POS[id];
        return <line key={id} className="grid-drop" x1={x} x2={x} y1={y} y2={sy + (sy < y ? 18 : -18)} />;
      })}
      {(['a1', 'a2', 'a3', 'b1', 'b2'] as BusId[]).map((b) => (
        <circle key={b} className="grid-pole" cx={POS[b][0]} cy={POS[b][1]} r={4} />
      ))}
      {tx('T1')}
      {STREETS.map((id) => tx(id))}
      {STREETS.map(street)}
      {settings.hubMw !== null && (
        <g
          className={`grid-el grid-hub${is({ kind: 'hub' }) ? ' is-selected' : ''}`}
          role="button"
          tabIndex={0}
          aria-label={`Charging hub, ${settings.hubMw} MW`}
          onClick={() => onSelect({ kind: 'hub' })}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect({ kind: 'hub' })}
        >
          <line className="grid-drop" x1={680} x2={752} y1={FEEDER_B_Y} y2={FEEDER_B_Y} />
          <rect x={752} y={FEEDER_B_Y - 16} width={32} height={32} rx={3} />
          <path d={`M ${770} ${FEEDER_B_Y - 10} l -7 11 h 6 l -4 10 l 9 -13 h -6 z`} />
          <text className="grid-label" x={768} y={FEEDER_B_Y - 24} textAnchor="middle">
            Hub {settings.hubMw} MW
          </text>
        </g>
      )}
      {level.controls.switches && <text className="grid-label grid-label--quiet" x={8} y={466}>Squares are switches: filled is closed</text>}
    </svg>
  );
}

/* ── The inspector ─────────────────────────────────────────────────────── */

function Inspector({
  level,
  settings,
  day,
  step,
  selected,
  change,
}: {
  level: GridLevel;
  settings: GridSettings;
  day: ReturnType<typeof runDay>;
  step: number;
  selected: Selection;
  change(patch: (s: GridSettings) => void): void;
}) {
  const series = (pick: (k: number) => number | undefined) => (day.ok ? day.steps.map((_, k) => pick(k) ?? 0) : []);

  if (selected.kind === 'street') {
    const name = BUSES.find((b) => b.id === selected.id)!.name;
    const vs = series((k) => (day.ok ? day.steps[k]!.v[selected.id] : undefined));
    const lo = Math.min(...vs);
    const hi = Math.max(...vs);
    return (
      <aside className="grid-inspector" aria-label="Inspector">
        <p className="label">Street, 400 V</p>
        <h3>{name}</h3>
        <p className="grid-inspector__now">
          {vs.length ? `${vs[step]!.toFixed(3)} pu at ${clock(step)}` : 'No supply'}
        </p>
        <Chart values={vs} step={step} min={0.9} max={1.1} band={[V_MIN, V_MAX]} unit="pu" />
        {vs.length > 0 && (
          <p className="grid-inspector__note">
            Lowest {lo.toFixed(3)}, highest {hi.toFixed(3)}. Allowed 0.950 to 1.050.
          </p>
        )}
        <p className="grid-inspector__note">Click its transformer, just above or below it, to change the tap.</p>
      </aside>
    );
  }

  if (selected.kind === 'tx') {
    const t = TRANSFORMERS.find((x) => x.id === selected.id)!;
    const loads = series((k) => (day.ok ? day.steps[k]!.loading[selected.id] : undefined));
    const can = selected.id === 'T1' ? level.controls.oltc : level.controls.taps;
    return (
      <aside className="grid-inspector" aria-label="Inspector">
        <p className="label">Transformer, {t.mva} MVA</p>
        <h3>{t.name}</h3>
        {selected.id === 'T1' ? (
          <div className="grid-control">
            <span>On-load tap: step {settings.oltc > 0 ? '+' : ''}{settings.oltc} ({(settings.oltc * OLTC_STEP * 100).toFixed(2)}%)</span>
            <span className="fseg">
              <button type="button" disabled={!can || settings.oltc <= OLTC_MIN} onClick={() => change((s) => (s.oltc -= 1))}>
                −
              </button>
              <button type="button" disabled={!can || settings.oltc >= OLTC_MAX} onClick={() => change((s) => (s.oltc += 1))}>
                +
              </button>
            </span>
          </div>
        ) : (
          <div className="grid-control">
            <span>Off-load tap, boosts the street</span>
            <span className="fseg">
              {STREET_TAPS.map((tap, k) => (
                <button
                  key={tap}
                  type="button"
                  className={settings.taps[selected.id as StreetId] === k ? 'is-on' : ''}
                  aria-pressed={settings.taps[selected.id as StreetId] === k}
                  disabled={!can}
                  onClick={() => change((s) => (s.taps[selected.id as StreetId] = k))}
                >
                  {tap > 0 ? '+' : ''}
                  {(tap * 100).toFixed(1)}%
                </button>
              ))}
            </span>
          </div>
        )}
        {!can && <p className="grid-inspector__note">Not something this level lets you change.</p>}
        <p className="grid-inspector__now">{loads.length ? `${Math.round(loads[step]! * 100)}% loaded at ${clock(step)}` : 'No supply'}</p>
        <Chart values={loads.map((x) => x * 100)} step={step} min={0} max={130} band={[0, 100]} unit="%" />
      </aside>
    );
  }

  if (selected.kind === 'line') {
    const l = LINES.find((x) => x.id === selected.id)!;
    const id = selected.id;
    const closed = settings.closed[id];
    const loads = series((k) => (day.ok ? day.steps[k]!.loading[id] : undefined));
    const ampsNow = day.ok ? day.steps[step]!.amps[id] : undefined;
    const cond: ConductorId = id === 'tie' ? 'light' : settings.conductors[id];
    return (
      <aside className="grid-inspector" aria-label="Inspector">
        <p className="label">11 kV line, {l.km} km</p>
        <h3>{l.name}</h3>
        <div className="grid-control">
          <span>Switch: {closed ? 'closed' : 'open'}</span>
          <Button disabled={!level.controls.switches} onClick={() => change((s) => (s.closed[id] = !s.closed[id]))}>
            {closed ? 'Open it' : 'Close it'}
          </Button>
        </div>
        {id !== 'tie' && (
          <div className="grid-control grid-control--stack">
            <span>Conductor</span>
            {CONDUCTOR_IDS.map((k) => (
              <label key={k} className="grid-radio">
                <input
                  type="radio"
                  name={`cond-${id}`}
                  checked={cond === k}
                  disabled={!level.controls.conductors}
                  onChange={() => change((s) => (s.conductors[id as Exclude<LineId, 'tie'>] = k))}
                />
                <span>
                  {CONDUCTORS[k].name}, {CONDUCTORS[k].amps} A
                  {CONDUCTORS[k].costPerKm > 0 ? ` · $${(CONDUCTORS[k].costPerKm * l.km).toLocaleString('en-US')}` : ' · as built'}
                </span>
              </label>
            ))}
          </div>
        )}
        {!level.controls.switches && !level.controls.conductors && <p className="grid-inspector__note">Not something this level lets you change.</p>}
        <p className="grid-inspector__now">
          {closed && ampsNow !== undefined ? `${Math.round(ampsNow)} A of ${CONDUCTORS[cond].amps} A at ${clock(step)}` : 'Open: carries nothing'}
        </p>
        {closed && <Chart values={loads.map((x) => x * 100)} step={step} min={0} max={130} band={[0, 100]} unit="%" />}
      </aside>
    );
  }

  return (
    <aside className="grid-inspector" aria-label="Inspector">
      <p className="label">Customer, on Feeder B pole 2</p>
      <h3>EV charging hub</h3>
      <div className="grid-control grid-control--stack">
        <span>Charging power agreed with the hub</span>
        <span className="fseg">
          {HUB_POWER_STOPS.map((mw) => (
            <button
              key={mw}
              type="button"
              className={settings.hubMw === mw ? 'is-on' : ''}
              aria-pressed={settings.hubMw === mw}
              disabled={!level.controls.hub}
              onClick={() => change((s) => (s.hubMw = mw))}
            >
              {mw} MW
            </button>
          ))}
        </span>
      </div>
      <p className="grid-inspector__note">
        The hub needs 12 MWh from 17:00. At {settings.hubMw} MW it charges for {settings.hubMw ? (12 / settings.hubMw).toFixed(1) : '—'} hours.
        A lower power runs longer into the night, when the town is quiet.
      </p>
    </aside>
  );
}

/** A day on one small chart: the series, the allowed band, and where the clock is. */
function Chart({ values, step, min, max, band, unit }: { values: number[]; step: number; min: number; max: number; band: [number, number]; unit: string }) {
  if (!values.length) return null;
  const W = 280;
  const H = 96;
  const x = (k: number) => 4 + (k / (STEPS - 1)) * (W - 8);
  const y = (v: number) => H - 14 - ((Math.max(min, Math.min(max, v)) - min) / (max - min)) * (H - 22);
  const d = values.map((v, k) => `${k ? 'L' : 'M'} ${x(k).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  return (
    <svg className="grid-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Through the day, in ${unit}`}>
      <rect className="grid-chart__band" x={4} width={W - 8} y={y(band[1])} height={y(band[0]) - y(band[1])} />
      <path className="grid-chart__line" d={d} fill="none" />
      <line className="grid-chart__now" x1={x(step)} x2={x(step)} y1={4} y2={H - 14} />
      {[0, 6, 12, 18].map((h) => (
        <text key={h} className="grid-chart__tick" x={x(h * 4)} y={H - 2}>
          {String(h).padStart(2, '0')}
        </text>
      ))}
    </svg>
  );
}
